import { useCallback, useEffect, useRef } from 'react';

/** Marks the loading overlay shown while the order re-syncs, so touching it doesn't count as the user starting. */
export const RESYNC_OVERLAY_ATTRIBUTE = 'data-resync-overlay';

/**
 * Records the first real user interaction inside the modal since it opened on the current order.
 * Only a pointer press or key press counts: derived writes (dynamic fields, pricing, geocoding)
 * never fire these, so unlike the draft's `overrides` or `isDirty`, this can tell the two apart.
 *
 * Spread `captureProps` on the dialog root so the header, tabs, and footer are all covered. The
 * flag is a ref because the re-sync reads it when a read resolves and nothing renders from it.
 *
 * @param {object} args
 * @param {boolean} args.open Whether the modal is open. Closing resets the flag.
 * @param {object} [args.order] The order prop. A new identity resets the flag.
 * @returns {{
 *   userStartedRef: { current: boolean },
 *   captureProps: { onPointerDownCapture: Function, onKeyDownCapture: Function },
 * }}
 */
export const useUserStarted = ({ open, order }) => {
    const userStartedRef = useRef(false);

    useEffect(() => {
        userStartedRef.current = false;
    }, [open, order]);

    const markStarted = useCallback((event) => {
        if (event.target?.closest?.(`[${RESYNC_OVERLAY_ATTRIBUTE}]`)) return;
        userStartedRef.current = true;
    }, []);

    return {
        userStartedRef,
        captureProps: { onPointerDownCapture: markStarted, onKeyDownCapture: markStarted },
    };
};
